import { yupResolver } from "@hookform/resolvers/yup";

import React, { useEffect, useState } from "react";

import isEmpty from "lodash.isempty";
import { observer } from "mobx-react";
import PropTypes from "prop-types";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";
import styled, { useTheme } from "styled-components";
import * as Yup from "yup";

import { useStores } from "../../contexts/StoreContext";
import { ApiClient } from "../../services/api";
import CustomSelect from "../../shared/CustomSelect/CustomSelect";
import Icon from "../../shared/Icon/Icon";
import Button from "../../shared/components/Button/Button";
import Input from "../../shared/components/Input/Input";
import LocationSelect from "../../shared/components/LocationSelect/LocationSelect";
import ModalCloseButton from "../../shared/components/Modal/ModalCloseButton";
import { useAnalytics } from "../../shared/helpers/AnalyticsTrackingContext";
import Markdown from "../../shared/styles/Markdown";

const StyledFormModal = styled.div`
  color: ${({ theme }) => theme.textColor};

  button {
    font-size: 1.5rem;
  }

  input {
    font-size: 1.5rem;
    padding-left: 8px;
    border-radius: 0;
  }

  input[type="checkbox"] {
    display: none;
  }
`;

const StyledFormHeader = styled.div`
  background-color: ${({ theme, backgroundColour }) =>
    backgroundColour || theme.secondaryBackgroundColor};
  min-height: 4rem;
`;

const StyledHeader = styled.span`
  color: ${({ theme }) => theme.negativeTextColor};
  margin: 0;
`;

const StyledLabel = styled.label`
  display: flex;
  flex-direction: column;
  margin: 15px 0;
  font-size: 1rem;
  font-weight: bold;
`;

const StyledError = styled.span`
  color: #ff0000;
`;

const StyledCheckbox = styled.div`
  margin: 0 13px 13px 0;
  span {
    width: 25px;
    height: 25px;
    display: flex;
    justify-content: center;
    align-items: center;
    font-size: 1.5rem;
    border: 2px solid ${({ error }) => (error ? "#ff0000" : "#000")};
    .bi-check-lg {
      display: none;
    }
  }

  input[type="checkbox"]:checked + span {
    .bi-check-lg {
      display: block;
    }
  }
`;

const StyledPersonalData = styled.div`
  color: ${({ theme }) => theme.textColor};
`;

const StyledConfirmation = styled.div`
  min-height: 100%;
`;

function JobAlertForm(props) {
  const {
    emailToSend,
    agreementContent,
    privacyPolicyLink,
    formHeaderBackgroundColour,
  } = props;
  const { sendTrackingEvent, sendEvent } = useAnalytics();
  const { rolesStore, modalStore, filtersStore } = useStores();
  const [isAlertRegistered, setIsAlertRegistered] = useState(false);

  const theme = useTheme();
  const { closeModal } = modalStore;
  const { filters } = filtersStore;
  const { location } = filters;
  const { roleOptions, rolesByRoleName } = rolesStore;
  const selectedRole = rolesByRoleName[filters.category];

  useEffect(() => {
    sendTrackingEvent("VACANCY_ALERT_SIGNUP");

    return () => {
      sendTrackingEvent("VACANCY_ALERT_SIGNUP_EXIT");
    };
  }, []);

  const validationSchema = Yup.object().shape({
    email: Yup.string().required("Email is required").email("Email is invalid"),
    firstName: Yup.string().required("First name is required"),
    locations: Yup.object()
      .shape({
        label: Yup.string(),
        location: Yup.object().shape({
          city: Yup.string(),
          country: Yup.string(),
          lat: Yup.string(),
          long: Yup.string(),
        }),
        value: Yup.object().shape({
          city: Yup.string(),
          country: Yup.string(),
          lat: Yup.string(),
          long: Yup.string(),
        }),
      })
      .required("Location is required"),
    role: Yup.string().required("Role is required"),
    surname: Yup.string().required("Surname is required"),
    termsAndCond:
      agreementContent && Yup.bool().oneOf([true], "Agreement is required"),
  });

  const selectedRoleValue = selectedRole
    ? {
        label: selectedRole?.roleName,
        value: selectedRole?.roleName,
      }
    : null;

  const formOptions = {
    defaultValues: {
      role: selectedRoleValue?.label || "",
      termsAndCond: false,
    },
    resolver: yupResolver(validationSchema),
  };
  const { register, handleSubmit, formState, setValue, clearErrors } =
    useForm(formOptions);
  const { errors } = formState;

  const onSubmit = handleSubmit(
    (data) => {
      const {
        email,
        firstName,
        surname,
        role,
        locations: {
          label,
          value: { addressLine1, city, county, postCode, country },
          location: {
            geoLocation: { coordinates },
          },
        },
      } = data;

      const formData = {
        firstName,
        surname,
        email,
        tenantId: process.env.TENANT_ID,
        location: {
          addressLine1: addressLine1 || "",
          city,
          county,
          postCode,
          country,
          geoLocation: {
            type: "Point",
            coordinates,
          },
          formattedAddress: label,
        },
        category: role,
      };

      ApiClient.registerJobAlert(
        formData,
        () => {
          sendTrackingEvent("VACANCY_ALERT_SIGNUP_SUCCESS");

          setIsAlertRegistered(true);
          setTimeout(() => {
            closeModal();
          }, 5000);
        },
        // eslint-disable-next-line no-console
        (error) => {
          if (error.response.status === 409) {
            toast("You are already subscribed to this job alert");
          } else {
            toast("Error during registration jobAlert", error.status);
            sendTrackingEvent("VACANCY_ALERT_SIGNUP_FAIL");
            console.log("Error during registration jobAlert", error.status);
          }
        }
      );
    },
    (err) => {
      const events = Object.entries(err).map(([key, val]) => {
        return {
          category: "CAREERS_SITE_ENAGEMENT",
          name: "FORM_QUESTION_VALIDATION_FAILURE",
          label: key,
          data: {
            message: val.message,
          },
        };
      });

      sendEvent(events);
    }
  );

  const roleSelectStyles = {
    container: (provided) => ({
      ...provided,
      height: "auto",
      width: "100%",
    }),
    control: (provided) => ({
      ...provided,
      borderBottom: "2px solid black",
      borderRadius: "none",
      borderWidth: "0 0 2px 0",
      boxShadow: "none",
      fontSize: "1.5rem",
      fontWeight: "400",
    }),
    option: (provided) => ({
      ...provided,
      fontSize: "1.5rem",
      fontWeight: "400",
    }),
  };

  const handleRoleSelect = (role) => {
    clearErrors("role");
    setValue("role", role.value);
  };

  const handleLocationSelect = (selectedLocation) => {
    clearErrors("locations");
    const locationObject = isEmpty(selectedLocation)
      ? null
      : {
          label: selectedLocation.label,
          location: {
            city: selectedLocation?.value?.city,
            country: selectedLocation?.value?.country,
            geoLocation: {
              coordinates: [
                selectedLocation?.value?.long,
                selectedLocation?.value?.lat,
              ],
            },
          },
          value: selectedLocation.value,
        };
    setValue("locations", locationObject);
  };

  return (
    <div className="justify-content-center overflow-auto" id="jobAlertModal">
      <StyledFormHeader
        className="d-flex align-items-center justify-content-between px-4"
        backgroundColour={formHeaderBackgroundColour}
      >
        <StyledHeader className="text-uppercase">
          Sign Up for Job Alerts
        </StyledHeader>
        <ModalCloseButton onClick={closeModal} />
      </StyledFormHeader>
      {isAlertRegistered ? (
        <StyledConfirmation className="d-flex justify-content-center align-items-center flex-column p-5 text-center">
          <h3 className="mb-5 fw-bolder">Good job!</h3>
          <h3 className="mb-5">We will send you job alerts!</h3>
          <Icon color={theme.primaryColor} icon="checkIcon" size={60} />
        </StyledConfirmation>
      ) : (
        <StyledFormModal className="d-flex flex-column pb-4 px-4">
          <form
            className="d-flex flex-column"
            data-testid="form"
            onSubmit={onSubmit}
          >
            <Input
              errors={errors}
              label="What is your first name?"
              name="firstName"
              register={register}
              onFocus={() => {
                sendTrackingEvent("QUESTION_START", "firstName");
              }}
              onBlur={() => {
                sendTrackingEvent("QUESTION_FINISH", "firstName");
              }}
              validate
            />

            <Input
              errors={errors}
              label="What is your surname?"
              name="surname"
              register={register}
              onFocus={() => {
                sendTrackingEvent("QUESTION_START", "surname");
              }}
              onBlur={() => {
                sendTrackingEvent("QUESTION_FINISH", "surname");
              }}
              validate
            />

            <Input
              defaultValue={emailToSend}
              errors={errors}
              label="What is your email address?"
              name="email"
              register={register}
              onFocus={() => {
                sendTrackingEvent("QUESTION_START", "email");
              }}
              onBlur={() => {
                sendTrackingEvent("QUESTION_FINISH", "email");
              }}
              validate
            />
            <StyledLabel htmlFor="role">
              Location
              <LocationSelect
                onSelect={handleLocationSelect}
                {...register("locations")}
                selectedValue={location?.label}
                onFocus={() => {
                  sendTrackingEvent("QUESTION_START", "location");
                }}
                onBlur={() => {
                  sendTrackingEvent("QUESTION_FINISH", "location");
                }}
                styles={{
                  container: (provided) => ({ ...provided, width: "100%" }),
                  control: (provided) => ({
                    ...provided,
                    border: "none",
                    borderBottom: errors.locations?.message
                      ? "2px red"
                      : "2px black",
                    borderBottomStyle: "solid",
                    borderRadius: 0,
                    boxShadow: "none",
                    fontSize: "1.5rem",
                    fontWeight: "400",
                    height: "3rem",
                  }),
                  menuPortal: (provided) => ({ ...provided, zIndex: 9999 }),
                }}
              />
              {errors.locations?.message ? (
                <StyledError error={errors.locations?.message}>
                  Location is required
                </StyledError>
              ) : null}
            </StyledLabel>

            <StyledLabel htmlFor="role">
              Roles
              <CustomSelect
                defaultValue={selectedRoleValue}
                {...register("role")}
                components={{
                  IndicatorSeparator: () => null,
                }}
                formOptions={formOptions}
                id="role"
                onChange={handleRoleSelect}
                options={roleOptions}
                styles={roleSelectStyles}
                onFocus={() => {
                  sendTrackingEvent("QUESTION_START", "role");
                }}
                onBlur={() => {
                  sendTrackingEvent("QUESTION_FINISH", "role");
                }}
              />
              {errors.role?.message ? (
                <StyledError error={errors.role?.message}>
                  Role is required
                </StyledError>
              ) : null}
            </StyledLabel>

            {agreementContent && (
              <div>
                <label className="d-flex flex-grow-1" htmlFor="terms">
                  <div>
                    <StyledCheckbox error={errors.termsAndCond?.message}>
                      <input
                        id="terms"
                        type="checkbox"
                        {...register("termsAndCond")}
                      />
                      <span>
                        <i className="bi bi-check-lg" />
                      </span>
                    </StyledCheckbox>
                  </div>
                  <Markdown>{agreementContent}</Markdown>
                </label>
                {errors.termsAndCond && (
                  <StyledError error={errors.termsAndCond?.message}>
                    {errors.termsAndCond?.message}
                  </StyledError>
                )}
              </div>
            )}

            <StyledPersonalData className="py-5 d-flex flex-column">
              <span className="text-uppercase">Your personal data</span>
              <span>
                We want to ensure you are aware of why we collect your personal
                data, where it is stored and how you can manage the data
                submitted to us within your application.
              </span>
              {privacyPolicyLink && (
                <a className="mt-3" href={privacyPolicyLink}>
                  Please click here to view our Recruitment Privacy Policy
                </a>
              )}
            </StyledPersonalData>
            <Button className="text-uppercase py-4" type="submit">
              Submit
            </Button>
          </form>
        </StyledFormModal>
      )}
    </div>
  );
}
JobAlertForm.defaultProps = {
  agreementContent: "",
  emailToSend: "",
};

JobAlertForm.propTypes = {
  agreementContent: PropTypes.string,
  emailToSend: PropTypes.string,
  privacyPolicyLink: PropTypes.string.isRequired,
};

export default observer(JobAlertForm);
